export const useImpersonation = () => {
  // rawToken is the library's internal Nuxt state for the access token.
  // Writing to it updates both the reactive state AND syncs to the auth.token cookie.
  // Directly writing the cookie bypasses the reactive state, so getSession() would still
  // send the old token — which is why we must use rawToken here.
  const { rawToken } = useAuthState();
  const originalToken = useCookie('auth.originalToken');
  const impersonatedUserUuid = useCookie('auth.impersonatedUserUuid');
  const impersonatedUserName = useCookie('auth.impersonatedUserName');
  const impersonatedOrgName = useCookie('auth.impersonatedOrgName');

  const isImpersonating = computed(() => !!originalToken.value);

  const { getSession } = useAuth();

  const resetOrgStores = () => {
    useCustomerStore().$reset();
    useLocationStore().$reset();
  };

  const startImpersonation = async (userUuid: string) => {
    const response = await useApi().post<{
      token: string;
      user: { uuid: string; name: string; email: string; organization: { name: string; customerNumber: string } };
    }>(`/admin/users/${userUuid}/impersonate`);

    if (!response.data?.token) return;

    if (!originalToken.value) {
      originalToken.value = rawToken.value;
    }
    impersonatedUserUuid.value = userUuid;
    impersonatedUserName.value = response.data.user.name;
    impersonatedOrgName.value = response.data.user.organization.name;
    rawToken.value = response.data.token;

    resetOrgStores();
    await getSession();
    await navigateTo('/');
  };

  const stopImpersonation = async () => {
    rawToken.value = originalToken.value ?? null;
    originalToken.value = null;
    impersonatedUserUuid.value = null;
    impersonatedUserName.value = null;
    impersonatedOrgName.value = null;

    resetOrgStores();
    await getSession();
    await navigateTo('/admin?tab=users');
  };

  const reImpersonate = async () => {
    const uuid = impersonatedUserUuid.value;
    if (!uuid) return;

    const response = await useApi().post<{ token: string }>(`/admin/users/${uuid}/impersonate`);
    if (response.data?.token) {
      rawToken.value = response.data.token;
    }
  };

  return {
    isImpersonating,
    startImpersonation,
    stopImpersonation,
    reImpersonate,
    impersonatedUserUuid,
    impersonatedUserName,
    impersonatedOrgName,
  };
};
